"use client";

import { useEffect, useMemo, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Loader2 } from "lucide-react";
import clsx from "clsx";
import {
  buildDocsHref,
  docsProducts,
  DOCS_PRODUCT_QUERY_PARAM,
  getProductIntroHref,
  isCommonHref,
  isDocsProductKey,
  isHrefInProductNavigation,
  resolveDocsProduct,
} from "@/config/navigation";

export function DocsProductTabs() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [pendingProduct, setPendingProduct] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const activeProduct = useMemo(
    () => resolveDocsProduct(searchParams.get(DOCS_PRODUCT_QUERY_PARAM), pathname || undefined),
    [pathname, searchParams]
  );

  useEffect(() => {
    if (!pathname || !isCommonHref(pathname)) {
      return;
    }

    const productParam = searchParams.get(DOCS_PRODUCT_QUERY_PARAM);
    if (!isDocsProductKey(productParam)) {
      startTransition(() => {
        router.replace(buildDocsHref(pathname, activeProduct, searchParams), { scroll: false });
      });
    }
  }, [activeProduct, pathname, router, searchParams, startTransition]);

  useEffect(() => {
    if (!isPending) {
      setPendingProduct(null);
    }
  }, [isPending]);

  if (!pathname) {
    return null;
  }

  return (
    <div className="mt-2">
      <div className="grid grid-cols-3 gap-1.5 rounded-[1.2rem] border border-[#dce6fb] bg-[#f7f9ff] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] dark:border-white/10 dark:bg-white/[0.03]">
        {docsProducts.map((product) => {
          const isActive = product.key === activeProduct;
          const isTargetPending = isPending && pendingProduct === product.key;
          const targetHref =
            isCommonHref(pathname) || isHrefInProductNavigation(product.key, pathname)
              ? buildDocsHref(pathname, product.key, searchParams)
              : buildDocsHref(getProductIntroHref(product.key), product.key, searchParams);

          return (
            <button
              key={product.key}
              type="button"
              disabled={isPending}
              aria-busy={isTargetPending}
              onClick={() => {
                if (isActive || isPending) {
                  return;
                }

                setPendingProduct(product.key);
                startTransition(() => {
                  router.push(targetHref, { scroll: false });
                });
              }}
              className={clsx(
                "flex items-center justify-center gap-1.5 rounded-[0.95rem] px-2 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-all duration-200 disabled:cursor-wait",
                isActive
                  ? "bg-[#335AFF] text-white shadow-[0_12px_22px_rgba(51,90,255,0.28)]"
                  : "text-[#52607a] hover:bg-white hover:text-[#1736aa] dark:text-[#9fb0c6] dark:hover:bg-white/[0.06] dark:hover:text-[#f8fafc]",
                isTargetPending && "opacity-90"
              )}
            >
              {isTargetPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {product.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
